/**
 * Variable to TreeNode Converter
 *
 * Converts FunnelVariable data to tree structure for rc-tree.
 * Groups variables by task (global vs task-specific).
 *
 * CRITICAL: Tree keys MUST be unique across the entire tree.
 * A variable with taskIds: ["taskA", "taskB"] appears in 2 groups —
 * each instance gets a unique key: `var-{groupId}-{variableId}`.
 * The PATH stays `@variable[{variableId}]` (deduplicated on output).
 */

import type { FunnelVariable } from '@segmently/schema-models/v2';
import type { VariableTreeNode } from './types';

const TYPE_LABELS: Record<string, string> = {
  string: 'text',
  number: 'number',
  boolean: 'yes/no',
  date: 'date',
  enum: 'choice',
  'enum[]': 'multi-choice',
};

/**
 * Build tree nodes from variables, grouped by task.
 * Each variable node has a UNIQUE key: `var-{groupId}-{variableId}`.
 */
export function variablesToTreeNodes(
  variables: Record<string, FunnelVariable>,
  tasks?: Array<{ id: string; name: string }>,
  /**
   * Follow model (strategy-intake-feasibility, AC-016): the routes currently
   * selected. Groups outside it stay visible and are marked «route not
   * selected», so an empty task folder reads as a consequence of the route
   * choice instead of as missing data. `undefined` = no marking.
   */
  selectedTaskIds?: ReadonlySet<string>
): VariableTreeNode[] {
  const taskMap = new Map<string, string>();
  if (tasks) {
    for (const t of tasks) {
      taskMap.set(t.id, t.name);
    }
  }

  // Deduplicate variables by id
  const uniqueVars = new Map<string, FunnelVariable>();
  for (const variable of Object.values(variables)) {
    if (variable.id && !uniqueVars.has(variable.id)) {
      uniqueVars.set(variable.id, variable);
    }
  }

  // Group variables
  const globalVars: FunnelVariable[] = [];
  const taskVars = new Map<string, FunnelVariable[]>();

  for (const variable of uniqueVars.values()) {
    const varTaskIds = (variable as any).taskIds as string[] | undefined;
    if (!varTaskIds || varTaskIds.length === 0) {
      globalVars.push(variable);
    } else {
      for (const taskId of varTaskIds) {
        if (!taskVars.has(taskId)) {
          taskVars.set(taskId, []);
        }
        taskVars.get(taskId)!.push(variable);
      }
    }
  }

  const nodes: VariableTreeNode[] = [];

  // Global group
  if (globalVars.length > 0) {
    nodes.push({
      key: 'group-global',
      title: `Global Variables (${globalVars.length})`,
      nodeType: 'group',
      checkable: true,
      selectable: false,
      children: globalVars.map((v) => makeNode(v, 'global')),
    });
  }

  // Task groups
  for (const [taskId, vars] of taskVars) {
    const taskName = taskMap.get(taskId) || taskId;
    const routeMarker = selectedTaskIds && !selectedTaskIds.has(taskId) ? ' — route not selected' : '';
    nodes.push({
      key: `group-task-${taskId}`,
      title: `Task: ${taskName} (${vars.length})${routeMarker}`,
      nodeType: 'group',
      checkable: true,
      selectable: false,
      children: vars.map((v) => makeNode(v, taskId)),
    });
  }

  return nodes;
}

/** Create a tree node with UNIQUE key: var-{groupId}-{variableId} */
function makeNode(variable: FunnelVariable, groupId: string): VariableTreeNode {
  const typeLabel = TYPE_LABELS[(variable as any).type] || (variable as any).type || '?';
  const requiredBadge = (variable as any).required ? ' — required' : '';
  const optionsCount = (variable as any).options?.length;
  const optionsSuffix = optionsCount ? ` [${optionsCount} options]` : '';

  return {
    key: `var-${groupId}-${variable.id}`,
    title: `${(variable as any).name || variable.id} (${typeLabel})${optionsSuffix}${requiredBadge}`,
    nodeType: 'variable',
    variableId: variable.id,
    variableType: (variable as any).type,
    isRequired: (variable as any).required,
    checkable: true,
    selectable: false,
    isLeaf: true,
  };
}

/**
 * Convert checked tree keys to variable paths.
 * Keys are `var-{groupId}-{variableId}` — extract variableId, deduplicate.
 */
export function nodeIdsToPaths(
  checkedKeys: string[],
  variables: Record<string, FunnelVariable>
): string[] {
  const seen = new Set<string>();
  const paths: string[] = [];

  for (const key of checkedKeys) {
    if (!key.startsWith('var-')) continue;
    // Extract variableId from key: var-{groupId}-{variableId}
    // groupId can contain hyphens (Firebase IDs), so split from end
    const parts = key.split('-');
    // key = "var" + "-" + groupId + "-" + variableId
    // But groupId might be "global" or a Firebase ID (no hyphens expected in var IDs)
    // Safest: strip "var-" prefix, then last segment after last "-" is NOT reliable
    // Better approach: check all variables and match
    const withoutPrefix = key.substring(4); // remove "var-"
    for (const [varId, _v] of Object.entries(variables)) {
      if (withoutPrefix.endsWith(`-${varId}`) || withoutPrefix === `global-${varId}`) {
        if (!seen.has(varId)) {
          seen.add(varId);
          paths.push(`@variable[${varId}]`);
        }
        break;
      }
    }
  }

  return paths;
}

/**
 * Convert variable paths to ALL matching tree keys.
 * One path `@variable[id]` may map to MULTIPLE keys (one per group).
 */
export function pathsToNodeIds(
  paths: string[],
  variables: Record<string, FunnelVariable>
): string[] {
  const varIds = new Set<string>();
  for (const path of paths) {
    const match = path.match(/^@variable\[([^\]]+)\]/);
    if (match) varIds.add(match[1]);
  }

  // For each varId, generate keys for ALL groups it belongs to
  const keys: string[] = [];
  for (const varId of varIds) {
    const variable = variables[varId];
    if (!variable) continue;
    const varTaskIds = (variable as any).taskIds as string[] | undefined;
    if (!varTaskIds || varTaskIds.length === 0) {
      keys.push(`var-global-${varId}`);
    } else {
      for (const taskId of varTaskIds) {
        keys.push(`var-${taskId}-${varId}`);
      }
    }
  }

  return keys;
}

/**
 * Count unique variables (not tree instances).
 */
export function countUniqueVariables(variables: Record<string, FunnelVariable>): number {
  const seen = new Set<string>();
  for (const v of Object.values(variables)) {
    if (v.id) seen.add(v.id);
  }
  return seen.size;
}
